import cn from 'classnames'
import getComponent from '../component-helper'
import { observer } from 'mobx-react'
import { action } from 'mobx'
import React, { useState } from 'react'
import { cssName } from '../css'
import { withStyle } from '../style-it'
import { useComponentContext } from './Context'
import Grid from './Grid'
import GridItem from './GridItem'
import { getEnv } from '@invisr/mobx-state-tree'
import merge from 'lodash/merge'
const PLS = Symbol('Polystack')

export function useComponentRef (component) {
  if (component.type === 'Custom') return
  const [ref, _setRef] = useState(null)

  const setRef = action(function setRef (_ref) {
    _setRef(_ref)
    if (component?.mapping) {
      component.setRef(_ref)
      if (_ref) component.setRenderProps(_ref.props)
      component.setState(_ref?.state)
    }

    if (ref) {
      if(!ref.setState?.[PLS]) {
        const fn = ref.setState?.bind(ref)

        const cb = function () {
          component.setState(ref.state)
        }

        ref.setState = function (partialState, callback) {
          return fn(partialState, () => {
            cb()
            callback && callback()
          })
        }
        ref.setState[PLS] = true
      }
    }
  })

  return setRef
}

const PortalComponent = observer(({
  component,
  index,
  settings,
  store,
  position,
  routes,
  overides,
  noGrid = false,
  toProps,
  location,
  ...renderProps
}) => {
  const { type, componentsOrderedLtr, components } = component
  const componentHtmlId = component.htmlId // getComponentHtmlId(index)
  const ctx = useComponentContext()
  const getRef = useComponentRef(component)
  const defaultChildren = componentsOrderedLtr?.length ? [
    <Grid
      className={cssName(component.name)}
      settings={ctx.store.settings}
      key='Grid'
      compactType={component.compactType}
      maxY={component.maxY}
    >
      {componentsOrderedLtr?.map(c => (
        <PortalComponent
          location={location}
          key={c.id}
          component={c}
        />
      ))}
    </Grid>
  ] : []

  const [PC] = useState(() => withStyle(getComponent(component)))
  toProps = toProps || component.toProps || {}

  const pcProps = {
    ...toProps,
    getRef,
    id: null,
    cmpId: component.id,
    _component: component,
    ...renderProps || {},
    ctx,
    scopeKey: componentHtmlId
  }

  const gridItemProps = {
    compactType: component.parent?.compactType,
    noGrid: !!noGrid,
    position: toProps.position || position || component.position,
    settings: ctx.store.settings,
    'data-component': type,
    component: type,
    className: cssName(component.name),
    isScrollable: /* isScrollable || */ toProps.isScrollable,
    sticky: toProps.sticky,
    styleClassName: cn(toProps.className, toProps.class, componentHtmlId),
    id: componentHtmlId
  }

  switch (type) {
    case 'Navigation':
      return (
        <GridItem {...gridItemProps}>
          <PC {...pcProps} location={location} items={ctx.routes}>{defaultChildren}</PC>
        </GridItem>
      )
    case 'Tabs':
      return (
        <GridItem {...gridItemProps}>
          <PC {...pcProps} tabs={components}>{
            components.map(c => (
              <Grid
                settings={ctx.store.settings}
                key={c.id}
                className={cssName(component.name)}
                compactType={c.compactType}
              >
                <PortalComponent component={c} />
              </Grid>))
          }
          </PC>
        </GridItem>
      )
    case 'Carousel':
      return (
        <GridItem {...gridItemProps}>
          <PC {...pcProps}>{
            components.map(c => (
              <PortalComponent
                key={c.id}
                component={c}
                settings={ctx.store.settings}
                compactType={c.compactType}
              />
            ))}
          </PC>
        </GridItem>)
    case 'Accordion':
      return (
        <GridItem {...gridItemProps}>
          <PC {...pcProps}>{
            components.map(c => (
              <PortalComponent
                component={c}
                key={c.id}
                settings={ctx.store.settings}
                compactType={c.compactType}
              />
            ))}
          </PC>
        </GridItem>)
    case 'ConditionalItem':
      return <PC
               {...pcProps}
               key={component.id}
               position={{ ...position, y: null }}
               index={index}
             >{defaultChildren}</PC>

    case 'Conditional':
      return (
      <GridItem {...gridItemProps}>
        <PC {...pcProps}>{
          components.map((c, index) => {
            return (
              <PortalComponent
                key={c.id}
                component={c}
                index={index}
              />
            )
          })
        }</PC>
      </GridItem>
      )
    case 'TimeLine':
    case 'CardList':
      return (
        <GridItem {...gridItemProps}>
          <PC {...pcProps}>{
            components.map(c => (
              <PortalComponent
                component={c}
                key={c.id}
              />
            ))
          }</PC>
        </GridItem>
      )
    case 'Repeater':
      return (
        <GridItem {...gridItemProps}>
          <PC {...pcProps} componentItems={components} childrenComponents={components?.map((c, index) =>
            observer(renderProps => (
              <PortalComponent
                noGrid
                component={c}
                index={index}
                {...renderProps}
              />
            ))
          )}>{[]}</PC>
        </GridItem>
      )
  }
  return (
    <GridItem {...gridItemProps}>
      <PC {...pcProps}>
        {defaultChildren}
      </PC>
    </GridItem>
  )
})

export default PortalComponent
